'use client';

import { c as _c } from "react/compiler-runtime";
import { mergeClasses } from 'minimal-shared/utils';
import { useRef, useState, useEffect } from 'react';
import { m, useTransform, useMotionValue, useAnimationFrame, useMotionTemplate } from 'framer-motion';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { createClasses } from 'src/theme/create-classes';

// ----------------------------------------------------------------------

const animateBorderClasses = {
  root: createClasses('border__animation__root'),
  primaryBorder: createClasses('border__animation__primary'),
  secondaryBorder: createClasses('border__animation__secondary'),
  svgWrapper: createClasses('border__animation__svg__wrapper'),
  movingShape: createClasses('border__animation__moving__shape')
};
export function AnimateBorder(t0) {
  const $ = _c(55);
  let children;
  let className;
  let duration;
  let other;
  let slotProps;
  let sx;
  if ($[0] !== t0) {
    ({
      children,
      duration,
      slotProps,
      className,
      sx,
      ...other
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = duration;
    $[4] = other;
    $[5] = slotProps;
    $[6] = sx;
  } else {
    children = $[1];
    className = $[2];
    duration = $[3];
    other = $[4];
    slotProps = $[5];
    sx = $[6];
  }
  const theme = useTheme();
  const rootRef = useRef(null);
  const primaryBorderRef = useRef(null);
  const [isHidden, setIsHidden] = useState(false);
  const secondaryBorderStyles = useComputedElementStyles(theme, primaryBorderRef);
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (rootRef.current) {
        const displayStyle = getComputedStyle(rootRef.current).display;
        setIsHidden(displayStyle === "none");
      }
    };
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleVisibility = t1;
  let t2;
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      handleVisibility();
      window.addEventListener("resize", handleVisibility);
      return () => window.removeEventListener("resize", handleVisibility);
    };
    t3 = [];
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== slotProps || $[11] !== theme) {
    t4 = typeof slotProps?.outlineColor === "function" ? slotProps?.outlineColor(theme) : slotProps?.outlineColor;
    $[10] = slotProps;
    $[11] = theme;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const outlineColor = t4;
  const t5 = slotProps?.svgSettings?.rx;
  const t6 = slotProps?.svgSettings?.ry;
  let t7;
  if ($[13] !== duration || $[14] !== isHidden || $[15] !== t5 || $[16] !== t6) {
    t7 = {
      duration,
      isHidden,
      rx: t5,
      ry: t6
    };
    $[13] = duration;
    $[14] = isHidden;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const borderProps = t7;
  let t8;
  if ($[18] !== borderProps || $[19] !== slotProps?.primaryBorder || $[20] !== theme.mixins) {
    t8 = () => slotProps?.primaryBorder && <MovingBorder {...borderProps} ref={primaryBorderRef} size={slotProps?.primaryBorder?.size} sx={[{
      ...theme.mixins.borderGradient({
        padding: slotProps?.primaryBorder?.width
      })
    }, ...(Array.isArray(slotProps?.primaryBorder?.sx) ? slotProps?.primaryBorder?.sx ?? [] : [slotProps?.primaryBorder?.sx])]} />;
    $[18] = borderProps;
    $[19] = slotProps?.primaryBorder;
    $[20] = theme.mixins;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const renderPrimaryBorder = t8;
  let t9;
  if ($[22] !== borderProps || $[23] !== secondaryBorderStyles || $[24] !== slotProps?.primaryBorder?.size || $[25] !== slotProps?.secondaryBorder || $[26] !== theme.mixins) {
    t9 = () => slotProps?.secondaryBorder && <MovingBorder {...borderProps} size={slotProps?.secondaryBorder?.size ?? slotProps?.primaryBorder?.size} sx={[{
      ...theme.mixins.borderGradient({
        padding: slotProps?.secondaryBorder?.width ?? secondaryBorderStyles.padding
      }),
      borderRadius: secondaryBorderStyles.borderRadius,
      transform: "scale(-1, -1)"
    }, ...(Array.isArray(slotProps?.secondaryBorder?.sx) ? slotProps?.secondaryBorder?.sx ?? [] : [slotProps?.secondaryBorder?.sx])]} />;
    $[22] = borderProps;
    $[23] = secondaryBorderStyles;
    $[24] = slotProps?.primaryBorder?.size;
    $[25] = slotProps?.secondaryBorder;
    $[26] = theme.mixins;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const renderSecondaryBorder = t9;
  let t10;
  if ($[28] !== className) {
    t10 = mergeClasses([animateBorderClasses.root, className]);
    $[28] = className;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== outlineColor || $[31] !== slotProps?.primaryBorder?.width || $[32] !== theme.mixins) {
    t11 = theme.mixins.borderGradient({
      color: outlineColor,
      padding: slotProps?.primaryBorder?.width
    });
    $[30] = outlineColor;
    $[31] = slotProps?.primaryBorder?.width;
    $[32] = theme.mixins;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== children) {
    t12 = !!children && {
      minWidth: "unset",
      minHeight: "unset"
    };
    $[34] = children;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12) {
    t13 = {
      minWidth: 40,
      minHeight: 40,
      overflow: "hidden",
      position: "relative",
      width: "fit-content",
      "&::before": t11,
      ...t12
    };
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== sx) {
    t14 = Array.isArray(sx) ? sx : [sx];
    $[39] = sx;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t13 || $[42] !== t14) {
    t15 = [t13, ...t14];
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== renderPrimaryBorder) {
    t16 = renderPrimaryBorder();
    $[44] = renderPrimaryBorder;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== renderSecondaryBorder) {
    t17 = renderSecondaryBorder();
    $[46] = renderSecondaryBorder;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== children || $[49] !== other || $[50] !== t10 || $[51] !== t15 || $[52] !== t16 || $[53] !== t17) {
    t18 = <Box dir="ltr" ref={rootRef} className={t10} sx={t15} {...other}>{t16}{t17}{children}</Box>;
    $[48] = children;
    $[49] = other;
    $[50] = t10;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  return t18;
}
function MovingBorder({
  ref,
  rx = '30%',
  ry = '30%',
  size,
  duration = 8,
  isHidden,
  sx,
  ...other
}) {
  const svgRectRef = useRef(null);
  const progress = useMotionValue(0);
  const updateAnimationFrame = time => {
    if (!svgRectRef.current) return;
    try {
      const pathLength = svgRectRef.current.getTotalLength();
      const pixelsPerMs = pathLength / (duration * 1000);
      progress.set(time * pixelsPerMs % pathLength);
    } catch {
      return;
    }
  };
  const calculateTransform = val => {
    if (!svgRectRef.current) return {
      x: 0,
      y: 0
    };
    try {
      const point = svgRectRef.current.getPointAtLength(val);
      return point ? {
        x: point.x,
        y: point.y
      } : {
        x: 0,
        y: 0
      };
    } catch {
      return {
        x: 0,
        y: 0
      };
    }
  };
  useAnimationFrame(time_0 => !isHidden ? updateAnimationFrame(time_0) : undefined);
  const x = useTransform(progress, val_0 => calculateTransform(val_0).x);
  const y = useTransform(progress, val_1 => calculateTransform(val_1).y);
  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;
  return <Box ref={ref} component="span" sx={[{
    textAlign: 'initial'
  }, ...(Array.isArray(sx) ? sx : [sx])]} {...other}>
      <svg width="100%" height="100%" preserveAspectRatio="none" xmlns="http://w3.org/2000/svg" className={animateBorderClasses.svgWrapper} style={{
      position: 'absolute'
    }}>
        <rect ref={svgRectRef} fill="none" width="100%" height="100%" rx={rx} ry={ry} />
      </svg>

      <Box component={m.span} style={{
      transform
    }} className={animateBorderClasses.movingShape} sx={{
      width: size,
      height: size,
      filter: 'blur(8px)',
      position: 'absolute',
      background: `radial-gradient(currentColor 40%, transparent 80%)`
    }} />
    </Box>;
}
function useComputedElementStyles(theme, ref) {
  const $ = _c(9);
  const isRtl = theme.direction === "rtl";
  const [computedStyles, setComputedStyles] = useState(null);
  let t0;
  if ($[0] !== isRtl || $[1] !== ref.current) {
    t0 = () => {
      if (ref.current) {
        const style = getComputedStyle(ref.current);
        setComputedStyles({
          paddingTop: style.paddingBottom,
          paddingBottom: style.paddingTop,
          paddingLeft: isRtl ? style.paddingLeft : style.paddingRight,
          paddingRight: isRtl ? style.paddingRight : style.paddingLeft,
          borderTopLeftRadius: isRtl ? style.borderBottomLeftRadius : style.borderBottomRightRadius,
          borderTopRightRadius: isRtl ? style.borderBottomRightRadius : style.borderBottomLeftRadius,
          borderBottomLeftRadius: isRtl ? style.borderTopLeftRadius : style.borderTopRightRadius,
          borderBottomRightRadius: isRtl ? style.borderTopRightRadius : style.borderTopLeftRadius
        });
      }
    };
    $[0] = isRtl;
    $[1] = ref.current;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== isRtl || $[4] !== ref) {
    t1 = [ref, isRtl];
    $[3] = isRtl;
    $[4] = ref;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useEffect(t0, t1);
  const t2 = `${computedStyles?.paddingTop} ${computedStyles?.paddingRight} ${computedStyles?.paddingBottom} ${computedStyles?.paddingLeft}`;
  const t3 = `${computedStyles?.borderTopLeftRadius} ${computedStyles?.borderTopRightRadius} ${computedStyles?.borderBottomRightRadius} ${computedStyles?.borderBottomLeftRadius}`;
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      padding: t2,
      borderRadius: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}